import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Partner } from 'src/app/models/Admin/Partner.model';
import { AdministrativeService } from 'src/app/services/administrative/administrative.service';

@Component({
  selector: 'app-partners-management',
  templateUrl: './partners-management.component.html',
  styleUrls: ['./partners-management.component.scss']
})
export class PartnersManagementComponent implements OnInit {

  @ViewChild('changeTrialPeriodDialog') changeTrialPeriodTemplate!: TemplateRef<any>;
  private changeTrialPeriodDialog!: MatDialogRef<any>;

  private lastValidFilter: { companyName: string } | null = null;

  public filters: FormGroup = new FormGroup({
    companyName: new FormControl("", [Validators.maxLength(100)])
  },);

  readonly displayedColumns: string[] = ['column-id', 'column-email', 'column-company-name', 'column-options'];
  public dataSource: Partner[] = [];
  public totalItems: number = 0;

  public pageSize: number = 20;
  public trialDaysForm!: FormGroup;
  public pageSizeOptions: number[] = [20, 50, 100];

  public selectedTrialPeriod: number | null = null;
  public changingTrialPeriod: boolean = false;

  public trialControl = new FormControl(null, [
    Validators.required,
    Validators.min(1),
    Validators.max(999)
  ]);

  constructor(
    private adminService: AdministrativeService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {}

  async ngOnInit(): Promise<void> {
    this.loadAccounts(0, this.pageSize);
    this.trialDaysForm = this.fb.group({
      trialPeriod: new FormControl( [
        Validators.min(1),
        Validators.max(999)
      ])
    });
  }

  clickFilter(useLastValidFilter: boolean = false) {
    if ((this.filters.invalid && !useLastValidFilter) ||
        (useLastValidFilter && this.lastValidFilter == null)) {
      this.snackBar.open(this.translateService.instant("admin.partner-management.errors.input-one-field"), '', {
        duration: 6000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
      return;
    }

    if (useLastValidFilter && this.lastValidFilter) {
      this.filters.controls['companyName'].setValue(this.lastValidFilter.companyName);
    }

    this.loadAccounts(0, this.pageSize);
  }

  limitTo3Digits(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.value.length > 3) {
      input.value = input.value.slice(0, 3);
      this.trialDaysForm.controls['trialPeriod'].setValue(Number(input.value), { emitEvent: false });
    }
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.value.length > 3) {
      input.value = input.value.slice(0, 3);
    }

    this.selectedTrialPeriod = Number(input.value) ?? null;
  }

  clickChangeTrialPeriod(partner: Partner) {
    this.changeTrialPeriodDialog = this.dialog.open(
      this.changeTrialPeriodTemplate,
      { data: { partner } }
    );

    this.changeTrialPeriodDialog.afterClosed().subscribe(() => {
      this.trialDaysForm.reset();
    });
  }

  confirmChangeTrialPeriod(partner: Partner) {
    if (this.trialDaysForm.invalid || this.changingTrialPeriod) {
      this.snackBar.open(
        this.translateService.instant("admin.partner-management.errors.invalid-trial-period"),
        '',
        { duration: 4000, verticalPosition: 'top', panelClass: ['red-snackbar'] }
      );
      return;
    }

    const trialValue = this.trialDaysForm.value.trialPeriod;
    this.changingTrialPeriod = true;

    this.adminService.updateTrialPeriod(partner.id, trialValue).then(success => {
      this.changingTrialPeriod = false;

      if (success) {
        this.snackBar.open(this.translateService.instant("admin.partner-management.success.update-trial-period"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.changeTrialPeriodDialog.close();
        this.clickFilter(true);
      } else {
        this.snackBar.open(this.translateService.instant("admin.partner-management.errors.update-trial-period"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        this.changeTrialPeriodDialog.close();
      }
    });
  }

  paginationChange(event: any) {
    this.loadAccounts(event.pageIndex, event.pageSize);
  }

  async loadAccounts(page: number, pageSize: number) {
    let ret = await this.adminService.getAllPartners(
      page, pageSize,
      null,
      null,
      this.filters.controls['companyName'].value
    );

    this.lastValidFilter = {
      companyName: this.filters.controls['companyName'].value
    };

    if (ret == null) {
      this.lastValidFilter = null;
    }

    this.dataSource = ret?.accounts ?? [];
    this.totalItems = ret?.totalItems ??0;
  }
}
